// Shared presentational bits for tool-result shape renderers.

import { useState, type ReactNode } from 'react';

const DEFAULT_LINE_CAP = 24;

export function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 text-micro text-muted-foreground">
      {children}
    </span>
  );
}

export function KeyValueGrid({ entries }: { entries: Array<[string, string]> }) {
  if (entries.length === 0) return null;
  return (
    <dl
      data-testid="tool-result-kv"
      className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-body"
    >
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-caption font-medium text-muted-foreground">{k}</dt>
          <dd className="break-words text-foreground/90">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ProseBlock({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div data-testid={testId} className="whitespace-pre-wrap break-words text-body text-foreground/90">
      {children}
    </div>
  );
}

export function CodeBlock({ text, testId }: { text: string; testId?: string }) {
  return (
    <pre
      data-testid={testId}
      className="max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-caption text-foreground/90"
    >
      {text}
    </pre>
  );
}

export function ClampedList({
  items,
  renderItem,
  testId,
  cap = DEFAULT_LINE_CAP,
}: {
  items: ReactNode[];
  renderItem?: (node: ReactNode, i: number) => ReactNode;
  testId?: string;
  cap?: number;
}) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll || items.length <= cap ? items : items.slice(0, cap);
  return (
    <div data-testid={testId} className="space-y-1.5">
      <ul className="space-y-1.5">
        {visible.map((item, i) => (
          <li key={i}>{renderItem ? renderItem(item, i) : item}</li>
        ))}
      </ul>
      {items.length > cap && (
        <button
          type="button"
          className="text-caption text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => setShowAll((s) => !s)}
        >
          {showAll ? 'Show less' : `Show ${items.length - cap} more`}
        </button>
      )}
    </div>
  );
}
